/**
 * AI Chat store 各 composable 共享的上下文类型。
 *
 * <p>主 store 创建所有响应式 state 与基础辅助函数后，把它们打包成本对象，
 * 传给 {@link ./useSessionLifecycle} / {@link ./useStreamOrchestrator} /
 * {@link ./usePendingConfirmations} / {@link ./usePendingUserQuestions} 等子 composable。</p>
 *
 * <p>这样做的目的：</p>
 * <ul>
 *   <li>子 composable 之间无循环依赖（都只依赖 context 类型 + ./types）</li>
 *   <li>state 所有权仍在主 store，对外 API 完全不变</li>
 *   <li>子 composable 只负责"行为"，不持有跨域状态</li>
 * </ul>
 */
import { shallowReactive, type ComputedRef, type Ref } from 'vue'
import type { AiStreamStateView } from '@/api/aiChat'
import type { useAiPlanStore } from '@/stores/aiPlan'
import type {
  AiUiMessage,
  PendingGatewayConfirmation,
  PendingUserQuestion,
  ResumableStop,
  UserActionItem
} from './types'

/** 用户主动取消时的 AbortController 标记类型。 */
export type CancellableAbort = AbortController & { _userCancelled?: boolean; _runId?: string }

/**
 * 按会话分桶的在途流注册表（key = 拥有该流的 sessionId，统一 trim）。
 *
 * <p>并发放开的基础设施：每个会话独立登记自己的 SSE AbortController，
 * 互不覆盖。响应式（shallowReactive Map）——派生状态（如 sending）依赖
 * has/register/delete 自动重算。存的 ctrl 不做深度代理，保持原始引用可比。</p>
 */
export interface StreamRegistry {
  has(sessionId: string | null | undefined): boolean
  get(sessionId: string | null | undefined): CancellableAbort | null
  register(sessionId: string, abortCtrl: CancellableAbort): void
  /** 流归属会话被服务端重定向时迁移登记 key（from == to 时为幂等空操作）。 */
  move(fromSessionId: string, toSessionId: string): void
  unregister(sessionId: string | null | undefined): void
  entries(): IterableIterator<[string, CancellableAbort]>
  clear(): void
}

export function createStreamRegistry(): StreamRegistry {
  const map = shallowReactive(new Map<string, CancellableAbort>())
  const keyOf = (id: string | null | undefined): string => (id ?? '').trim()
  return {
    has(sessionId) {
      const key = keyOf(sessionId)
      return key !== '' && map.has(key)
    },
    get(sessionId) {
      const key = keyOf(sessionId)
      return key === '' ? null : (map.get(key) ?? null)
    },
    register(sessionId, abortCtrl) {
      const key = keyOf(sessionId)
      if (key) {
        map.set(key, abortCtrl)
      }
    },
    move(fromSessionId, toSessionId) {
      const from = keyOf(fromSessionId)
      const to = keyOf(toSessionId)
      if (!from || !to || from === to) {
        return
      }
      const ctrl = map.get(from)
      if (ctrl) {
        map.delete(from)
        map.set(to, ctrl)
      }
    },
    unregister(sessionId) {
      map.delete(keyOf(sessionId))
    },
    entries() {
      return map.entries()
    },
    clear() {
      map.clear()
    }
  }
}

/**
 * AI Chat store 子 composable 共享的上下文。
 *
 * <p>主 store 在调用各子 composable 前组装好本对象；子 composable 只读取/写入本对象中的
 * 响应式 ref 与辅助函数，不再自己创建跨域状态。</p>
 */
export interface AiChatContext {
  // ── 核心响应式 state ──
  sessionId: Ref<string | null>
  messages: Ref<AiUiMessage[]>
  /** 当前查看的会话是否有在途流（主 store 由 streamsBySession 派生，子模块只读） */
  sending: Ref<boolean>
  /** 当前查看的会话是否有待处理卡（确认/提问）（主 store 由 userActionQueue 派生，子模块只读） */
  awaitingUserAction: Ref<boolean>
  lastError: Ref<string | null>
  recoverableStreamState: Ref<AiStreamStateView | null>
  /** 上一回合异常停止后的可继续状态（提示条数据源）；null = 无可继续中断 */
  resumableStop: Ref<ResumableStop | null>
  confirmationHttpPending: Ref<boolean>

  /** 跨会话消息快照缓存（非响应式，按引用共享） */
  messagesBySession: Map<string, AiUiMessage[]>

  // ── 用户行动队列（来自 useUserActionQueue） ──
  userActionQueue: Ref<UserActionItem[]>
  enqueueUserAction: (item: UserActionItem) => void
  clearUserActionQueue: () => void
  clearPendingConfirmation: () => void
  pendingConfirmation: ComputedRef<PendingGatewayConfirmation | null>
  pendingUserQuestion: ComputedRef<PendingUserQuestion | null>
  pendingQuestionQueue: ComputedRef<PendingUserQuestion[]>
  stageAnswer: (
    pendingQuestionId: string,
    answer: { selectedOptionId?: string | null; customAnswer?: string | null }
  ) => void
  getAllStagedAnswers: () => Array<{
    pendingQuestionId: string
    selectedOptionId?: string | null
    customAnswer?: string | null
  }>
  resetQuestionState: () => void
  goToNextQuestion: () => void
  goToPrevQuestion: () => void

  // ── 在途流注册表（按会话分桶，支持多个会话同时流式） ──
  streamsBySession: StreamRegistry

  // ── 消息管理辅助（来自 useMessages） ──
  appendAssistantMessage: (content: string) => void
  reconcileStaleStreamMessages: (targetSessionId: string) => void

  // ── 关联 store ──
  aiPlanStore: ReturnType<typeof useAiPlanStore>
}
